import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type {
  GetForms200,
  GetFormsId200,
  GetFormsQueryParams,
} from '@eureca/recruiter-api-sdk/types';

export type FormListEntry = GetForms200['items'][number];
export type FormDetail = GetFormsId200;

export class FormsFetchError extends Error {
  readonly name = 'FormsFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

const STALE_TIME_MS = 60_000;

export function formsListQueryKey(params: GetFormsQueryParams) {
  return ['recruiter', 'forms', 'page', params] as const;
}

export function formsListQueryOptions(params: GetFormsQueryParams) {
  return queryOptions({
    queryKey: formsListQueryKey(params),
    queryFn: async ({ signal }) => {
      const res = await fetchClient<GetForms200>({
        method: 'GET',
        url: '/forms',
        params,
        signal,
      });
      if (res.error) {
        throw new FormsFetchError(
          `Failed to load forms (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
    retry: (failureCount, err) => {
      if (err instanceof FormsFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function formByIdQueryKey(id: string) {
  return ['recruiter', 'forms', 'detail', id] as const;
}

export function formByIdQueryOptions(id: string) {
  return queryOptions({
    queryKey: formByIdQueryKey(id),
    queryFn: async ({ signal }) => {
      const res = await fetchClient<FormDetail>({
        method: 'GET',
        url: `/forms/${id}`,
        signal,
      });
      if (res.error) {
        throw new FormsFetchError(
          `Failed to load form (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    staleTime: STALE_TIME_MS,
  });
}
